/**
 * The fire solution: railgun early-out, hitscan aim, re-engagement from
 * memory, or the skill-scaled grid search (coarse sweep, gravity-assist and
 * teammate seeds, focused grids, refinement) over the trajectory scorer.
 */

import { isTrajectoryPreviewDisabled } from '../../core/matchEvents';
import type { PlayerState, GameState, BotConfig, BotFogContext, Vec3, WeaponKey } from '../../types';
import type { BotFireResult, AimCandidate, ScoringContext, BatchScorer } from './types';
import { vecLength, vecSub, vecNormalize, vecDot, vecScale, vecAdd, vec3, surfaceTangentFrame } from '../../math';
import { WEAPONS, PHYS_DT } from '../../constants';
import { getPlayerWorldPosition } from '../../core/gameState';
import { raycastTerrain } from '../../core/surface';
import type { SeededRandom } from '../../core/random';
import { getPlayerWorldPos, hasAmmo, getAliveEnemies } from './common';
import { selectTarget, selectWeapon, expectedTargetPos, expectedEnemyHealth } from './targeting';
import { shouldScoutProbe, computeScoutProbe } from './scouting';
import { scoreTrajectory, FULL_FLIGHT_STEPS } from './scoring';
import { getTeammateFireSeeds } from './intel';
import { tuningFor, fireSearchTier } from './tuning';
import { simulateRound, type RoundRecord } from '../../core/roundSim';
import { maxFirePower } from '../../core/stabilizers';
import { dasin, datan2, dcos, dsin } from '../../core/dmath';
import { fireMissionFor } from '../../core/squads';

/**
 * §15.2: a shot at a team-picture track is aimed with the track's precision — the believed point moved by a seeded
 * draw inside the track's spread, on the surface — so a radar dot shoots wider than a fresh identified sighting.
 */
function aimWithSpread(pos: Vec3, planetPos: Vec3, planetRadius: number, spread: number, rng: SeededRandom): Vec3 {
  if (spread <= 0) return pos;
  const rel = vecSub(pos, planetPos);
  const len = vecLength(rel);
  const n = vecNormalize(rel);
  const { tx, ty } = surfaceTangentFrame(n);
  const r = spread * Math.sqrt(rng.next()), th = 2 * Math.PI * rng.next();
  const d = vecNormalize(vecAdd(n, vecScale(vecAdd(vecScale(tx, dcos(th)), vecScale(ty, dsin(th))), r / Math.max(1e-6, planetRadius))));
  return vecAdd(planetPos, vecScale(d, len));
}

/**
 * Compute bot fire parameters using trajectory simulation grid search.
 * Skill controls grid search depth and aim noise.
 * With fogContext, may fire a scout probe if blind.
 */
export function computeBotFire(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  rng: SeededRandom,
  fogContext?: BotFogContext,
  scorer?: BatchScorer
): BotFireResult | null {
  // Round flows: the team's joint plan already chose this unit's shot (ai/bot/teamPlan.ts)
  if (fogContext?.round?.plannedShot) return { ...fogContext.round.plannedShot };
  const flowT = tuningFor(config).flow;
  const orders = fogContext?.round?.teamOrders;
  const queued = !!orders && orders.length > 0 && !!fogContext?.round;
  // One dry run of the team's queue serves both the expected-damage targeting and the queue-aware planner
  const after = queued && (flowT.expectedDamageTargeting || flowT.planAgainstQueue)
    ? simulateRound(gameState, orders!, { launchStaggerSteps: 0, prepaid: true }).state
    : null;
  // Expected-damage targeting (owner, Oct 2026): a target the queue already kills is not worth a shot
  const ctx: BotFogContext | undefined = after && flowT.expectedDamageTargeting && fogContext?.round
    ? { ...fogContext, round: { ...fogContext.round, expectedEnemyHealth: expectedEnemyHealth(player, gameState, after, fogContext, tuningFor(config)) } }
    : fogContext;
  const current = computeBotFireAgainst(player, gameState, config, rng, ctx, scorer);
  // Phase 49 — plan against the queue: teammates' shots this round will crater and launch things before or
  // while this one flies. Search the world they leave as well, and keep whichever shot the round says does more.
  if (!flowT.planAgainstQueue || !after || !current) return current;
  if (WEAPONS[current.weaponKey].hitscan || current.weaponKey === 'probe') return current;
  const meAfter = after.players.find(p => p.id === player.id);
  if (!meAfter || !meAfter.alive || meAfter.airborne) return current;
  const predictedState: GameState = { ...after, phase: 'aiming', projectile: null };
  const predicted = computeBotFireAgainst(meAfter, predictedState, config, rng, ctx, scorer);
  if (!predicted || WEAPONS[predicted.weaponKey].hitscan || predicted.weaponKey === 'probe') return current;
  // What a round is worth to the team: enemy damage, kills and launches by the given flights' owners
  const roundValue = (rec: ReturnType<typeof simulateRound>, owners: (id: number) => boolean): number => {
    let v = 0;
    for (const i of rec.impacts) {
      if (!owners(rec.flights[i.flightIndex].ownerId)) continue;
      for (const d of i.damages) { const p = gameState.players.find(x => x.id === d.playerId); if (p && p.teamIndex !== player.teamIndex) v += d.damage; }
      for (const id of i.kills) { const p = gameState.players.find(x => x.id === id); if (p && p.teamIndex !== player.teamIndex) v += 40; }
      for (const id of i.launched) { const p = gameState.players.find(x => x.id === id); if (p && p.teamIndex !== player.teamIndex) v += 10; }
    }
    return v;
  };
  const withMine = (fire: BotFireResult) => simulateRound(gameState, [...(orders ?? []), { playerId: player.id, fire: { params: { azimuth: fire.azimuth, elevation: fire.elevation, power: fire.power, weaponKey: fire.weaponKey } } }], { launchStaggerSteps: 0, prepaid: true });
  // Team marginal value: what the round does for the team with my shot, minus what it did without it — so a
  // shot that launches the target a teammate's later-landing shot was aimed at counts as the loss it is
  const teamOwner = (id: number) => gameState.players.find(x => x.id === id)?.teamIndex === player.teamIndex;
  const baseline = flowT.teamMarginalValue ? roundValue(simulateRound(gameState, orders ?? [], { launchStaggerSteps: 0, prepaid: true }), teamOwner) : 0;
  const value = (fire: BotFireResult): number => flowT.teamMarginalValue
    ? roundValue(withMine(fire), teamOwner) - baseline
    : roundValue(withMine(fire), id => id === player.id);
  const vCurrent = value(current);
  const vPredicted = value(predicted);
  let chose: 'current' | 'predicted' | 'retargeted' = vPredicted > vCurrent ? 'predicted' : 'current';
  let chosen = chose === 'predicted' ? predicted : current;
  let best = Math.max(vCurrent, vPredicted);
  // Wasteful for the team — it adds nothing, or it destroys a meaningful share of what it does on its own by
  // launching or killing a target a teammate's later-landing shot was aimed at: one more search, against
  // another target, keeps whichever adds more for the team
  const standalone = flowT.teamMarginalValue ? roundValue(withMine(chosen), id => id === player.id) : 0;
  const wasteful = best <= 0 || best < standalone * flowT.researchBelowFraction;
  if (flowT.teamMarginalValue && wasteful && current.targetPos && ctx?.round) {
    const enemies = gameState.players.filter(p => p.alive && p.teamIndex !== player.teamIndex);
    let wasted: PlayerState | null = null, bd = Infinity;
    for (const e of enemies) { const d = vecLength(vecSub(getPlayerWorldPos(e, gameState.planets), current.targetPos)); if (d < bd) { bd = d; wasted = e; } }
    if (wasted && enemies.length > 1) {
      const retargetCtx: BotFogContext = { ...ctx, round: { ...ctx.round, avoidTargetIds: [...(ctx.round.avoidTargetIds ?? []), wasted.id] } };
      const alt = computeBotFireAgainst(player, gameState, config, rng, retargetCtx, scorer);
      if (alt && !WEAPONS[alt.weaponKey].hitscan && alt.weaponKey !== 'probe') {
        const vAlt = value(alt);
        if (vAlt > best) { chose = 'retargeted'; chosen = alt; best = vAlt; }
      }
    }
  }
  return { ...chosen, queuePlan: { chose, current: vCurrent, predicted: vPredicted } };
}

/**
 * Phase 60: what a round is worth to `teamIndex` — enemy damage, 40 per enemy kill (by blast, or launched and
 * lost), 10 per enemy launched — minus the same for the team's own units. With `known` set, only enemies
 * the team can account for (visible or ghost) count: a shot is never credited for a unit nobody saw.
 */
export function roundShotValue(rec: RoundRecord, players: readonly PlayerState[], teamIndex: number, known: ReadonlySet<number> | null): number {
  const teamOf = (id: number) => players.find(x => x.id === id)?.teamIndex;
  const credit = (id: number, amount: number): number => {
    const t = teamOf(id);
    if (t === undefined) return 0;
    if (t === teamIndex) return -amount;
    return !known || known.has(id) ? amount : 0;
  };
  let v = 0;
  for (const i of rec.impacts) {
    for (const d of i.damages) v += credit(d.playerId, d.damage);
    for (const id of i.kills) v += credit(id, 40);
    for (const id of i.launched) v += credit(id, 10);
  }
  for (const a of rec.airborne) if (a.cause === 'knockback' && a.died) v += credit(a.playerId, 40);
  return v;
}

function computeBotFireAgainst(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  rng: SeededRandom,
  fogContext?: BotFogContext,
  scorer?: BatchScorer
): BotFireResult | null {
  const tuning = tuningFor(config);
  const tf = tuning.fire;
  // Stabilizers: a unit that moved this turn cannot fire above its cap, so never search above it
  let powerCap = maxFirePower(player, gameState.config);
  let elevMin = 0.1;
  // Command override: fireProbe → skip shouldScoutProbe check, go straight to probe
  const cmd = player.pendingCommand;
  if (cmd?.type === 'fireProbe' && hasAmmo(player, 'probe')) {
    return computeScoutProbe(player, gameState, config, rng, fogContext ?? {
      visibleEnemyIds: [], ghosts: [], turnNumber: gameState.turnNumber,
    });
  }

  // Check if bot should scout instead of attack
  // §15.2: a fire mission comes before scouting — the team already knows where to shoot
  if (!fireMissionFor(gameState, player.id) && shouldScoutProbe(player, gameState, config, fogContext)) {
    return computeScoutProbe(player, gameState, config, rng, fogContext!);
  }

  // === RAILGUN EARLY-OUT: close same-planet enemy in LOS → hitscan, skip trajectory search ===
  // Only triggers within 15 units — beyond that, missiles are better (area damage, arc).
  // This early-out bypasses the expensive trajectory search (~5400 sims) for close encounters.
  const RAILGUN_EARLY_OUT_RANGE = tf.railgunRange;
  if (hasAmmo(player, 'punch') && WEAPONS.punch.hitscan) {
    const myPos = getPlayerWorldPos(player, gameState.planets);
    const enemies = getAliveEnemies(player, gameState.players)
      .filter(e => e.planetIndex === player.planetIndex);

    if (enemies.length > 0) {
      const planet = gameState.planets[player.planetIndex];
      // Find closest same-planet enemy with clear line-of-fire
      let bestEnemy: PlayerState | null = null;
      let bestDist = Infinity;
      for (const enemy of enemies) {
        const enemyPos = getPlayerWorldPosition(enemy, gameState.planets);

        const toEnemy = vecSub(enemyPos, myPos);
        const dist = vecLength(toEnemy);

        // Skip enemies beyond railgun close-combat range
        if (dist > RAILGUN_EARLY_OUT_RANGE) continue;

        // Hitscan fires from 1.2 above surface — compute actual launch point
        const surfaceNormal = vecNormalize(player.surfaceDir);
        const launchPos = vecAdd(myPos, vecScale(surfaceNormal, 1.2));

        // LOS check targets hitbox center (0.5 above surface), not surface position.
        // At close range, a ray to surface position grazes the planet sphere → false block.
        const enemyNormal = vecNormalize(vecSub(enemyPos, planet.position));
        const hitboxCenter = vecAdd(enemyPos, vecScale(enemyNormal, 0.5));
        const toLaunch = vecSub(hitboxCenter, launchPos);
        const launchDist = vecLength(toLaunch);
        const launchDir = vecNormalize(toLaunch);

        // Terrain-aware LOS: any planet surface (including our own hills) between
        // the muzzle and the hitbox blocks the beam. The old sphere test both
        // missed hills and falsely blocked close shots that grazed the sphere.
        if (raycastTerrain(launchPos, launchDir, Math.max(0, launchDist - 0.4), gameState.planets)) {
          continue; // terrain blocks the shot
        }
        if (dist < bestDist) {
          bestDist = dist;
          bestEnemy = enemy;
        }
      }

      if (bestEnemy) {
        // Direct hitscan aim at this enemy — aim from launch point (1.2 above surface)
        const { skill } = config;
        const normal = vecNormalize(player.surfaceDir);
        const launchPt = vecAdd(myPos, vecScale(normal, 1.2));
        const enemyPos = getPlayerWorldPosition(bestEnemy, gameState.planets);
        const enemyNormal = vecNormalize(vecSub(enemyPos, planet.position));
        const hitboxCenter = vecAdd(enemyPos, vecScale(enemyNormal, 0.5));
        const toTarget = vecNormalize(vecSub(hitboxCenter, launchPt));

        const { tx, ty } = surfaceTangentFrame(normal);

        const tangent = vecSub(toTarget, vecScale(normal, vecDot(toTarget, normal)));
        const azimuth = vecLength(tangent) > 0.001
          ? datan2(vecDot(tangent, ty), vecDot(tangent, tx))
          : 0;
        const elevation = dasin(Math.max(-1, Math.min(1, vecDot(toTarget, normal))));

        const aimNoise = (1 - skill) * tf.hitscanAimNoise;
        const noisyAz = azimuth + (rng.next() - 0.5) * aimNoise;
        const noisyEl = Math.max(-Math.PI / 4, elevation + (rng.next() - 0.5) * aimNoise);

        return {
          azimuth: noisyAz,
          elevation: noisyEl,
          power: 50,
          weaponKey: 'punch',
          targetPos: enemyPos,
        };
      }
    }
  }

  const target = selectTarget(player, gameState, config, rng, fogContext);

  // Determine target position — either a real target, a commanded location, or blind fire
  let targetPos: Vec3;
  let targetPlanetIndex: number;
  let isBlindFire = false;

  // §15.2: a fire mission ahead of everything the bot would choose itself — shell the point, with the mission's spread
  const mission = fireMissionFor(gameState, player.id);
  if (mission && gameState.planets[mission.planetIndex]?.alive && mission.planetIndex !== player.planetIndex) {
    const pl = gameState.planets[mission.planetIndex];
    targetPos = aimWithSpread(vecAdd(pl.position, vecScale(vecNormalize(mission.surfaceDir), pl.radius)), pl.position, pl.radius, mission.spread ?? 0, rng);
    targetPlanetIndex = mission.planetIndex;
  } else
  // Command override: fireAtLocation → aim at the commanded planet center
  if (cmd?.type === 'fireAtLocation' && cmd.targetPlanetIndex !== undefined) {
    const cmdPlanet = gameState.planets[cmd.targetPlanetIndex];
    if (cmdPlanet && cmdPlanet.alive) {
      // Aim at planet surface point nearest to the bot
      const myPos = getPlayerWorldPos(player, gameState.planets);
      const toBot = vecNormalize(vecSub(myPos, cmdPlanet.position));
      targetPos = vecAdd(cmdPlanet.position, vecScale(toBot, cmdPlanet.radius));
      targetPlanetIndex = cmd.targetPlanetIndex;
    } else if (target) {
      targetPos = expectedTargetPos(target, gameState.planets, config, fogContext);
      targetPlanetIndex = target.planetIndex;
    } else {
      return null;
    }
  } else if (target) {
    targetPos = expectedTargetPos(target, gameState.planets, config, fogContext);
    targetPlanetIndex = target.planetIndex;
    // §15.2: a target known from the team picture (not in sight) is aimed with its track's precision
    const pt = fogContext?.pictureTargets?.find(t => t.unitId === target.id);
    if (pt && !fogContext!.visibleEnemyIds.includes(target.id)) {
      const pl = gameState.planets[targetPlanetIndex];
      targetPos = aimWithSpread(targetPos, pl.position, pl.radius, pt.spread, rng);
    }
  } else if (fogContext?.fireContacts?.length) {
    // 37.20 #5: shot at by someone the team cannot name — shoot back where the fire came from
    const c = fogContext.fireContacts.reduce((a, b) => (b.turnSeen > a.turnSeen ? b : a));
    let bi = -1, bd = Infinity;
    for (let i = 0; i < gameState.planets.length; i++) {
      const p = gameState.planets[i];
      if (p.type !== 'planet' || !p.alive) continue;
      const d = vecLength(vecSub(c.position, p.position)) - p.radius;
      if (d < bd) { bd = d; bi = i; }
    }
    if (bi < 0 || bi === player.planetIndex) return null;
    targetPos = c.position;
    targetPlanetIndex = bi;
    isBlindFire = true;
  } else if (fogContext?.pictureTargets?.some(t => t.unitId === undefined && t.planetIndex !== player.planetIndex)) {
    // §15.2: an anonymous contact in the team picture (a tower's radar dot, a sound): fire on the most confident one,
    // as wide as its precision
    const c = fogContext.pictureTargets.filter(t => t.unitId === undefined && t.planetIndex !== player.planetIndex)
      .reduce((a, b) => (b.confidence > a.confidence || (b.confidence === a.confidence && b.turnSeen > a.turnSeen) ? b : a));
    const pl = gameState.planets[c.planetIndex];
    targetPos = aimWithSpread(vecAdd(pl.position, vecScale(c.surfaceDir, pl.radius)), pl.position, pl.radius, c.spread, rng);
    targetPlanetIndex = c.planetIndex;
    isBlindFire = true;
  } else if (fogContext && getAliveEnemies(player, gameState.players).length > 0) {
    // Can't see anyone but enemies exist — blind fire at a random planet
    const otherPlanets = gameState.planets
      .map((p, i) => ({ planet: p, index: i }))
      .filter(({ planet, index }) =>
        planet.type === 'planet' && planet.alive && index !== player.planetIndex
      );
    if (otherPlanets.length === 0) return null;
    const pick = otherPlanets[rng.int(0, otherPlanets.length - 1)];
    // Aim at a random surface point
    const randDir = vecNormalize(vec3(
      rng.next() - 0.5,
      rng.next() - 0.5,
      rng.next() - 0.5
    ));
    targetPos = vecAdd(pick.planet.position, vecScale(randDir, pick.planet.radius));
    targetPlanetIndex = pick.index;
    isBlindFire = true;
  } else {
    return null;
  }

  let weaponKey = isBlindFire
    ? selectWeapon(player, config, rng) // no target context for blind fire
    : selectWeapon(player, config, rng, target!, gameState, fogContext);

  // Blind fire: don't waste utility weapons (tether, terraformer, antigrav) on unknown targets
  if (isBlindFire && WEAPONS[weaponKey].damage === 0) {
    weaponKey = 'standard';
  }

  // Check ammo
  if (!hasAmmo(player, weaponKey)) {
    // Fallback to standard
    if (!hasAmmo(player, 'standard')) return null;
  }

  let finalWeapon: WeaponKey = hasAmmo(player, weaponKey) ? weaponKey : 'standard';

  // Drill missile strategy: the drill creates a crater at the ANTIPODAL point of impact.
  // So aim at the opposite side of the planet from the enemy — the antipodal crater hits them.
  // §12.7: a weapon's own power cap and elevation floor bound the whole search (the field class)
  powerCap = Math.min(powerCap, WEAPONS[finalWeapon].maxPower ?? Infinity);
  elevMin = WEAPONS[finalWeapon].minElevation ?? 0.1;
  if (finalWeapon === 'drill' && !isBlindFire) {
    const targetPlanet = gameState.planets[targetPlanetIndex];
    const enemyDir = vecNormalize(vecSub(targetPos, targetPlanet.position));
    targetPos = vecAdd(targetPlanet.position, vecScale(enemyDir, -targetPlanet.radius));
  }

  const myPos = getPlayerWorldPos(player, gameState.planets);
  const { skill } = config;

  // === HITSCAN PATH: direct aim at target, no trajectory search ===
  if (WEAPONS[finalWeapon].hitscan && !isBlindFire) {
    const normal = vecNormalize(player.surfaceDir);
    const launchPt = vecAdd(myPos, vecScale(normal, 1.2));
    // Aim at target hitbox center (0.5 above surface)
    const targetSurfacePos = targetPos;
    const targetPlanet = gameState.planets[targetPlanetIndex];
    const targetNormal = vecNormalize(vecSub(targetSurfacePos, targetPlanet.position));
    const hitboxCenter = vecAdd(targetSurfacePos, vecScale(targetNormal, 0.5));

    const toTarget = vecNormalize(vecSub(hitboxCenter, launchPt));

    // Compute azimuth/elevation in local surface frame
    const { tx, ty } = surfaceTangentFrame(normal);

    const tangent = vecSub(toTarget, vecScale(normal, vecDot(toTarget, normal)));
    const azimuth = vecLength(tangent) > 0.001
      ? datan2(vecDot(tangent, ty), vecDot(tangent, tx))
      : 0;
    const elevation = dasin(Math.max(-1, Math.min(1, vecDot(toTarget, normal))));

    // Only fire the beam if terrain doesn't block it; otherwise fall back to an arcing shot
    const beamDist = vecLength(vecSub(hitboxCenter, launchPt));
    const beamBlocked = raycastTerrain(launchPt, toTarget, Math.max(0, beamDist - 0.4), gameState.planets) !== null;
    if (!beamBlocked) {
      // Add skill-based noise: lower skill = more scatter
      const aimNoise = (1 - skill) * tf.hitscanAimNoise;
      const noisyAz = azimuth + (rng.next() - 0.5) * aimNoise;
      const noisyEl = Math.max(-Math.PI / 4, elevation + (rng.next() - 0.5) * aimNoise);

      return {
        azimuth: noisyAz,
        elevation: noisyEl,
        power: 50, // irrelevant for hitscan but needed for FireParams
        weaponKey: finalWeapon,
        targetPos,
      };
    }
    finalWeapon = 'standard';
  }

  // Re-engagement: if targeting the same position as last turn, reuse stored aim params
  // (skip expensive grid search). This makes bots "lock on" to confirmed positions.
  // Weapon is re-selected fresh each turn to avoid locking onto one munition type.
  if (!isBlindFire && player.botLastFireParams && player.botLastFireTargetPos) {
    const lastTargetDist = vecLength(vecSub(targetPos, player.botLastFireTargetPos));
    const stored0 = player.botLastFireParams;
    // Stored aim is only valid if the bot still stands where it fired from
    const samePlace = stored0.fromPlanetIndex === undefined
      || (stored0.fromPlanetIndex === player.planetIndex && !!stored0.fromSurfaceDir
          && vecDot(vecNormalize(stored0.fromSurfaceDir), vecNormalize(player.surfaceDir)) > tf.reengageSameSpotDot);
    if (lastTargetDist < tf.reengageDistance && samePlace) {
      const stored = player.botLastFireParams;
      const aimNoise = (1 - skill) * (1 - skill);
      const skillDamp = 1 - skill * tf.skillDamp;
      const ghostNoise = fogContext && target && !fogContext.visibleEnemyIds.includes(target.id) ? tf.ghostNoise * skillDamp : 0;
      const totalNoise = aimNoise + ghostNoise;
      return {
        azimuth: stored.azimuth + (rng.next() - 0.5) * totalNoise * tf.reengageAzimuthScale,
        elevation: Math.max(elevMin, Math.min(1.3,
          stored.elevation + (rng.next() - 0.5) * totalNoise * tf.reengageElevationScale)),
        power: Math.max(5, Math.min(powerCap,
          stored.power + (rng.next() - 0.5) * totalNoise * tf.reengagePowerScale)),
        weaponKey: finalWeapon,
        targetPos, // keep the lock-on memory (previously dropped, so bots alternated search/reuse)
      };
    }
  }

  // Initial distance-based guess (from original bot AI)
  const normal = vecNormalize(player.surfaceDir);
  const toTarget = vecNormalize(vecSub(targetPos, myPos));

  // Project onto tangent plane for azimuth
  const tangentComponent = vecSub(toTarget, vecScale(normal, vecDot(toTarget, normal)));
  let guessAzimuth = 0;
  if (vecLength(tangentComponent) > 0.001) {
    const { tx, ty } = surfaceTangentFrame(normal);
    guessAzimuth = datan2(vecDot(tangentComponent, ty), vecDot(tangentComponent, tx));
  }

  // Skill-scaled parameter space search: higher skill → more sims → better accuracy.
  //
  // Expert (1.0): 24×8×6 coarse + 8×7³ focused + 6×5³ refine  = ~4650 sims (12 passes bought 0.6 % — sweeps/2026-10-09-refine-passes.md)
  // Hard   (0.8): 18×6×5 coarse + 6×5³ focused + 6×5³ refine  = ~2050 sims
  // Medium (0.5): 12×5×4 coarse + 4×5³ focused + 4×5³ refine  = ~1250 sims
  // Easy   (0.2):  8×4×3 coarse + 2×5³ focused + 0 refine     = ~350 sims

  // Grid dimensions scale with skill (tuning.fire.searchTiers)
  // R14 (b): a solar flare takes the preview offline for people; a bot loses its dial-in alike — the coarsest tier
  // (no refinement, no random seeds), the way a person aims without the line
  const tierNow = isTrajectoryPreviewDisabled(gameState) ? tuning.fire.searchTiers[tuning.fire.searchTiers.length - 1] : fireSearchTier(tuning, skill);
  const { coarseAzSteps, coarseElevSteps, coarsePowSteps, focusedTopN, focusedGridSize, refinePasses, randomSeeds } = tierNow;

  const dist = vecLength(vecSub(targetPos, myPos));

  // Helper: compute azimuth from bot surface toward a world position
  function azimuthTo(worldPos: Vec3): number {
    const toPos = vecNormalize(vecSub(worldPos, myPos));
    const tang = vecSub(toPos, vecScale(normal, vecDot(toPos, normal)));
    if (vecLength(tang) < 0.001) return guessAzimuth;
    const { tx: ttx, ty: tty } = surfaceTangentFrame(normal);
    return datan2(vecDot(tang, tty), vecDot(tang, ttx));
  }

  const seeds: AimCandidate[] = [];

  // --- Phase 1a: Coarse sweep of parameter space ---
  for (let ai = 0; ai < coarseAzSteps; ai++) {
    const az = (Math.PI * 2 * ai) / coarseAzSteps;
    for (let ei = 0; ei < coarseElevSteps; ei++) {
      const elev = elevMin + ((1.3 - elevMin) * ei) / (coarseElevSteps - 1);
      for (let pi = 0; pi < coarsePowSteps; pi++) {
        const pow = 10 + (90 * pi) / (coarsePowSteps - 1);
        seeds.push({ azimuth: az, elevation: elev, power: pow, score: 0 });
      }
    }
  }

  // --- Phase 1b: Gravity assist seeds ---
  // Use 5 closest bodies to avoid O(N) scaling on large maps.
  const fireGravityBodies: Array<{ az: number; ratio: number }> = [];
  for (let i = 0; i < gameState.planets.length; i++) {
    if (i === player.planetIndex || i === targetPlanetIndex) continue;
    const body = gameState.planets[i];
    const bodyDist = vecLength(vecSub(body.position, myPos));
    fireGravityBodies.push({ az: azimuthTo(body.position), ratio: Math.min(bodyDist / 80, 1) });
  }
  fireGravityBodies.sort((a, b) => a.ratio - b.ratio);
  for (const body of fireGravityBodies.slice(0, 5)) {
    for (const azOff of [-0.3, -0.15, 0, 0.15, 0.3]) {
      seeds.push({ azimuth: body.az + azOff, elevation: 0.3 + body.ratio * 0.4, power: 15 + body.ratio * 60, score: 0 });
      seeds.push({ azimuth: body.az + azOff, elevation: 0.5 + body.ratio * 0.3, power: 25 + body.ratio * 45, score: 0 });
    }
  }

  // --- Phase 1c: Per-turn random exploration ---
  for (let i = 0; i < randomSeeds; i++) {
    seeds.push({
      azimuth: rng.next() * Math.PI * 2,
      elevation: elevMin + rng.next() * (1.3 - elevMin),
      power: 10 + rng.next() * 90,
      score: 0,
    });
  }

  // --- Phase 1d: Teammate dial-in seeds ---
  const teamSeeds = getTeammateFireSeeds(player, gameState, targetPos, tuning.intel.teammateSeedDistance);
  for (const ts of teamSeeds) {
    seeds.push({ azimuth: guessAzimuth, elevation: ts.elevation, power: ts.power, score: 0 });
  }

  // --- Build batch scorer (WASM or JS fallback) ---
  // 37.19: search flights are capped (a shot that has not landed by then almost never scores) and
  // the best capped-out candidates are re-flown at full length as finalists (below)
  const searchSteps = Math.min(FULL_FLIGHT_STEPS, Math.max(1, Math.round(tf.searchFlightSeconds / PHYS_DT)));
  const scoringCtx: ScoringContext = {
    player, planets: gameState.planets, targetPos, targetPlanetIndex,
    weaponKey: finalWeapon, allPlayers: gameState.players,
    maxSteps: searchSteps, escapeTest: tf.escapeTest,
  };
  const allScored: AimCandidate[] = [];
  const batchScore = (candidates: AimCandidate[], maxSteps = searchSteps) => {
    const ctx = maxSteps === scoringCtx.maxSteps ? scoringCtx : { ...scoringCtx, maxSteps };
    if (scorer) {
      scorer(candidates, ctx);
    } else {
      for (const c of candidates) {
        c.score = scoreTrajectory(
          player, gameState.planets, targetPos, targetPlanetIndex,
          c.azimuth, c.elevation, c.power, finalWeapon,
          gameState.players, maxSteps, tf.escapeTest,
        );
      }
    }
    if (maxSteps === searchSteps) for (const c of candidates) allScored.push(c);
  };

  // --- Quick-evaluate all seeds ---
  batchScore(seeds);

  for (const sd of seeds) sd.power = Math.max(5, Math.min(powerCap, sd.power));

  // --- Phase 2: Focused search around top seeds ---
  seeds.sort((a, b) => a.score - b.score);
  const topSeeds = seeds.slice(0, focusedTopN);

  let bestCandidate: AimCandidate = { ...topSeeds[0] };
  for (const seed of topSeeds) {
    const seedAzRange = 0.6;
    const seedElevRange = 0.6;
    const seedPowRange = 40;

    const focused: AimCandidate[] = [];
    for (let ai = 0; ai < focusedGridSize; ai++) {
      for (let ei = 0; ei < focusedGridSize; ei++) {
        for (let pi = 0; pi < focusedGridSize; pi++) {
          const az = seed.azimuth - seedAzRange / 2 + (seedAzRange * ai) / (focusedGridSize - 1);
          const elev = seed.elevation - seedElevRange / 2 + (seedElevRange * ei) / (focusedGridSize - 1);
          const pow = seed.power - seedPowRange / 2 + (seedPowRange * pi) / (focusedGridSize - 1);

          focused.push({
            azimuth: az,
            elevation: Math.max(elevMin, Math.min(1.3, elev)),
            power: Math.max(5, Math.min(powerCap, pow)),
            score: 0,
          });
        }
      }
    }
    batchScore(focused);
    for (const c of focused) {
      if (c.score < bestCandidate.score) {
        bestCandidate = { ...c };
      }
    }
  }

  // --- Phase 3: Refinement passes around best overall ---
  let refAzRange = 0.6 / (focusedGridSize - 1);
  let refElevRange = 0.6 / (focusedGridSize - 1);
  let refPowRange = 40.0 / (focusedGridSize - 1);

  for (let pass = 0; pass < refinePasses; pass++) {
    refAzRange /= 2;
    refElevRange /= 2;
    refPowRange /= 2;

    const cAz = bestCandidate.azimuth;
    const cElev = bestCandidate.elevation;
    const cPow = bestCandidate.power;

    const refine: AimCandidate[] = [];
    for (let ai = 0; ai < 5; ai++) {
      for (let ei = 0; ei < 5; ei++) {
        for (let pi = 0; pi < 5; pi++) {
          const az = cAz - refAzRange / 2 + (refAzRange * ai) / 4;
          const elev = cElev - refElevRange / 2 + (refElevRange * ei) / 4;
          const pow = cPow - refPowRange / 2 + (refPowRange * pi) / 4;

          refine.push({
            azimuth: az,
            elevation: Math.max(elevMin, Math.min(1.3, elev)),
            power: Math.max(5, Math.min(powerCap, pow)),
            score: 0,
          });
        }
      }
    }
    batchScore(refine);
    for (const c of refine) {
      if (c.score < bestCandidate.score) {
        bestCandidate = { ...c };
      }
    }
  }

  // --- Finalists: the chosen shot and the best capped-out candidates re-fly at full length, so the
  // winner's score is exactly what the game flies and a long shot the cap cut short can still win
  if (searchSteps < FULL_FLIGHT_STEPS) {
    const capped = allScored
      .filter(c => c.score >= 500 && isFinite(c.score))
      .sort((a, b) => a.score - b.score)
      .slice(0, Math.max(0, tf.refliedFinalists));
    const finalists: AimCandidate[] = [{ ...bestCandidate }, ...capped.map(c => ({ ...c }))];
    batchScore(finalists, FULL_FLIGHT_STEPS);
    bestCandidate = finalists.reduce((b, c) => (c.score < b.score ? c : b), finalists[0]);
  }

  // No viable shot found — don't fire
  if (!isFinite(bestCandidate.score)) return null;

  // --- Phase 60: round-scored finalists. The best candidates by miss distance are re-judged by the round
  // each would start — the real flight with craters, knockback, launches, kills and collateral, with the
  // team's queued shots in flight — and the most valuable wins. The value counts only enemies the team
  // knows about (visible or ghost), so the judgement reads no more than the search's candidates did.
  if (tf.roundScoredCandidates > 0 && !WEAPONS[finalWeapon].hitscan && finalWeapon !== 'probe') {
    const pool = allScored.filter(c => isFinite(c.score)).sort((a, b) => a.score - b.score);
    const picked: AimCandidate[] = [{ ...bestCandidate }];
    for (const c of pool) {
      if (picked.length >= tf.roundScoredCandidates) break;
      if (picked.some(p => Math.abs(p.azimuth - c.azimuth) < 0.05 && Math.abs(p.elevation - c.elevation) < 0.05 && Math.abs(p.power - c.power) < 3)) continue;
      picked.push({ ...c });
    }
    const orders = fogContext?.round?.teamOrders ?? [];
    const known = fogContext ? new Set<number>([...fogContext.visibleEnemyIds, ...fogContext.ghosts.map(g => g.playerId)]) : null;
    const sim = (c?: AimCandidate) => simulateRound(gameState,
      c ? [...orders, { playerId: player.id, fire: { params: { azimuth: c.azimuth, elevation: c.elevation, power: c.power, weaponKey: finalWeapon } } }] : orders,
      { launchStaggerSteps: 0, prepaid: true });
    const baseline = orders.length ? roundShotValue(sim(), gameState.players, player.teamIndex, known) : 0;
    let bestValue = -Infinity;
    let chosen = bestCandidate;
    for (const c of picked) {
      const v = roundShotValue(sim(c), gameState.players, player.teamIndex, known) - baseline;
      if (v > bestValue || (v === bestValue && c.score < chosen.score)) { bestValue = v; chosen = c; }
    }
    bestCandidate = chosen;
  }

  // Degrade optimal shot with skill-based noise
  // Quadratic falloff: skill 1.0→0, 0.8→0.04, 0.5→0.25, 0.2→0.64, 0→1.0
  const aimNoise = (1 - skill) * (1 - skill);

  // Ghost/blind targets get extra aim scatter (stale or unknown position)
  // Scale by (1-skill): experts extrapolate ghost positions well
  const skillDamp = 1 - skill * tf.skillDamp; // default: expert→0.2, mid→0.6, low→0.84
  const ghostNoise = isBlindFire ? tf.blindNoise * skillDamp
    : (fogContext && target && !fogContext.visibleEnemyIds.includes(target.id)) ? tf.ghostNoise * skillDamp
    : 0;
  const totalNoise = aimNoise + ghostNoise;

  const finalAzimuth = bestCandidate.azimuth + (rng.next() - 0.5) * totalNoise * tf.finalAzimuthScale;
  const finalElevation = Math.max(elevMin, Math.min(1.3,
    bestCandidate.elevation + (rng.next() - 0.5) * totalNoise * tf.finalElevationScale));
  const finalPower = Math.max(5, Math.min(powerCap,
    bestCandidate.power + (rng.next() - 0.5) * totalNoise * tf.finalPowerScale));

  return {
    azimuth: finalAzimuth,
    elevation: finalElevation,
    power: finalPower,
    weaponKey: finalWeapon,
    targetPos,
  };
}
